import { useState, useEffect, useMemo, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { getPurchases, getProjects, exportPurchasesExcel, updatePurchaseOrderFields } from '../api/client'
import { TextCell, PillCell } from '../components/purchases/CeldasEditables'
import { FULFILLMENT_STATUSES, FULFILLMENT_STYLE, PAYMENT_STATUSES, PAYMENT_STYLE } from '../utils/purchaseStatus'
import { isPmOfAny } from '../utils/pm'
import { lineTotals } from '../utils/purchaseTotals'
import { currentLocale } from '../i18n'
import ColumnFilter from '../components/ColumnFilter'
import TruncatedCell from '../components/TruncatedCell'
import ImportarSolicitudes from '../components/purchases/ImportarSolicitudes'
import { ControlZoom, ZonaZoom } from '../components/ot/zoom'
import { useZoom } from '../components/ot/useZoom'

const CURRENCIES = ['CLP', 'USD', 'EUR', 'CNY', 'BRL', 'GBP', 'AUD']

const STATE_STYLE = {
  draft:     'bg-gray-100 text-gray-600',
  submitted: 'bg-amber-100 text-amber-700',
  approved:  'bg-blue-100 text-blue-700',
  sap:       'bg-violet-100 text-violet-700',
  rejected:  'bg-red-100 text-red-700',
  cancelled: 'bg-orange-100 text-orange-700',
  ordered:   'bg-indigo-100 text-indigo-700',
  received:  'bg-emerald-100 text-emerald-700',
  closed:    'bg-gray-200 text-gray-700',
}

const STATE_KEYS = ['draft', 'submitted', 'approved', 'sap', 'rejected', 'cancelled', 'ordered', 'received', 'closed']
const PENDIENTES = ['draft', 'submitted', 'approved', 'sap', 'ordered', 'received']

function fmtMoney(n, currency) {
  if (n == null || isNaN(n)) return '—'
  return `${Number(n).toLocaleString(currentLocale(), { maximumFractionDigits: 2 })} ${currency}`
}

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' })
}

// Clicking an inactive column starts it at its most useful direction: newest
// order first for ID, most recent activity first for Updated.
const SORT_DEFAULT_DIR = { id: 'desc', updated_at: 'desc', supplier: 'asc' }

// Named `ms`, not `t` — `t` is the i18n translator everywhere else in this file.
function timestamp(iso) {
  if (!iso) return null
  const ms = new Date(iso).getTime()
  return Number.isFinite(ms) ? ms : null
}

function compareBy(key, dir, a, b) {
  const mul = dir === 'asc' ? 1 : -1
  if (key === 'updated_at') {
    const ta = timestamp(a.updated_at)
    const tb = timestamp(b.updated_at)
    // A row with no usable timestamp sinks to the bottom either way, so missing
    // data never displaces real data at the top of the table.
    if (ta == null && tb == null) return b.id - a.id
    if (ta == null) return 1
    if (tb == null) return -1
    if (ta !== tb) return (ta - tb) * mul
    return b.id - a.id
  }
  if (key === 'supplier') {
    // By supplier, without one last; the same supplier keeps the newest request first.
    const sa = (a.supplier || '').trim().toLowerCase()
    const sb = (b.supplier || '').trim().toLowerCase()
    if (!sa && !sb) return b.id - a.id
    if (!sa) return 1
    if (!sb) return -1
    return sa.localeCompare(sb) * mul || b.id - a.id
  }
  // key === 'id'
  return (a.id - b.id) * mul
}

// The lines of a request: its items, or the single line kept in the request itself.
const lineasDe = (p) => (p.is_multi && p.items?.length ? p.items : [{
  id_rem: p.id_rem, item_description: p.item_description, quantity: p.quantity, unit: p.unit,
  estimated_unit_price: p.estimated_unit_price, final_unit_price: null, currency: p.currency }])

function SortHeader({ label, sortKey, sort, onSort, t, className = '' }) {
  const active = sort.key === sortKey
  const nextDir = active
    ? (sort.dir === 'asc' ? 'desc' : 'asc')
    : SORT_DEFAULT_DIR[sortKey]
  const actionLabel = t(nextDir === 'asc' ? 'list.sort.asc' : 'list.sort.desc', { col: label })
  return (
    <th
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={actionLabel}
        aria-label={actionLabel}
        className="inline-flex items-center gap-1 font-semibold text-gray-500 hover:text-gray-900 transition-colors"
      >
        {label}
        {/* The glyph slot is always rendered — only hidden — so the header row
            does not shift horizontally as the user clicks between columns. */}
        <span className={active ? '' : 'invisible'} aria-hidden="true">
          {active && sort.dir === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  )
}

// The requests seen item by item: one row per requested line, each with the request it belongs to.
function TablaItems({ filas, navigate, t }) {
  const [orden, setOrden] = useState({ k: 'solicitud', dir: 'desc' })
  const valorDe = { codigo: f => (f.l.id_rem || '').toLowerCase(), descripcion: f => (f.l.item_description || '').toLowerCase(),
    cantidad: f => Number(f.l.quantity) || 0, solicitud: f => f.p.id, proveedor: f => (f.p.supplier || '').toLowerCase() }
  const ordenadas = useMemo(() => {
    const get = valorDe[orden.k]
    const m = orden.dir === 'asc' ? 1 : -1
    return [...filas].sort((a, b) => {
      const x = get(a), y = get(b)
      return (x < y ? -1 : x > y ? 1 : 0) * m || (a.p.id - b.p.id) || (a.i - b.i)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filas, orden])
  const cab = (k, etiqueta, derecha) => (
    <th className={`px-3 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap ${derecha ? 'text-right' : 'text-left'}`}>
      <button type="button" onClick={() => setOrden(o => ({ k, dir: o.k === k && o.dir === 'asc' ? 'desc' : 'asc' }))} className="hover:text-gray-900">
        {etiqueta} <span className={orden.k === k ? '' : 'invisible'}>{orden.dir === 'asc' ? '▲' : '▼'}</span>
      </button>
    </th>
  )
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
      <table className="w-full min-w-[1100px] text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            {cab('codigo', t('list.items.code'))}
            {cab('descripcion', t('list.items.description'))}
            {cab('cantidad', t('list.items.qty'), true)}
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 text-right whitespace-nowrap">{t('list.items.price')}</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 text-right whitespace-nowrap">{t('list.items.lineTotal')}</th>
            {cab('solicitud', t('list.items.request'))}
            {cab('proveedor', t('list.cols.supplier'))}
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 text-left">{t('list.cols.project')}</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 text-left">{t('list.cols.state')}</th>
          </tr>
        </thead>
        <tbody>
          {ordenadas.map(({ p, l, i }) => {
            const precio = l.final_unit_price ?? l.estimated_unit_price
            const moneda = l.currency || p.currency
            return (
              <tr key={`${p.id}-${i}`} onClick={() => navigate(`/purchases/${p.id}`)}
                className="border-b border-gray-50 hover:bg-gray-50/60 cursor-pointer last:border-b-0">
                <td className="px-3 py-2 font-mono text-xs text-gray-700 whitespace-nowrap">{l.id_rem || '—'}</td>
                <td className="px-3 py-2 text-gray-900 max-w-[28rem]"><TruncatedCell text={l.item_description || null} className="max-w-[28rem]" /></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{l.quantity}{l.unit ? ` ${l.unit}` : ''}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-gray-600">{precio != null ? fmtMoney(precio, moneda) : '—'}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-gray-900 font-medium">{precio != null ? fmtMoney(precio * (Number(l.quantity) || 0), moneda) : '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs font-mono text-gray-500">#{p.id}</td>
                <td className="px-3 py-2 text-gray-700"><TruncatedCell text={p.supplier || null} className="max-w-[12rem]" /></td>
                <td className="px-3 py-2 text-gray-700"><TruncatedCell text={p.project_reference || null} className="max-w-[10rem]" /></td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATE_STYLE[p.state]}`}>{t(`states.${p.state}`) || p.state}</span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default function Purchases({ hideDownload = false } = {}) {
  const { t } = useTranslation(['purchases', 'common'])
  const navigate = useNavigate()
  const { user } = useAuth()
  const roles = user?.roles || (user?.role ? [user.role] : [])
  const isAdmin = roles.includes('admin')
  const canRequest = true  // every authenticated user may create a purchase request
  const canPurchase = isAdmin || roles.includes('purchaser')

  const [projects, setProjects] = useState([])
  // A user who is the PM of any project may use the (project-scoped) inbox even
  // without the global pm_po role.
  const isProjectPmAnywhere = useMemo(() => isPmOfAny(user, projects), [user, projects])

  const availableScopes = useMemo(() => {
    const s = []
    if (canRequest) s.push({ id: 'mine', label: t('list.scopes.mine') })
    s.push({ id: 'visible', label: t('list.scopes.visible') })
    // The approval inbox is for those who can actually decide: the assigned
    // PM/PO of a project (or an admin). The global pm_po role alone does not
    // grant it.
    if (isAdmin || isProjectPmAnywhere) s.push({ id: 'inbox', label: t('list.scopes.inbox') })
    if (isProjectPmAnywhere) s.push({ id: 'projects', label: t('list.scopes.projects') })
    if (canPurchase) s.push({ id: 'orders', label: t('list.scopes.orders') })
    if (canPurchase) s.push({ id: 'closed', label: t('list.scopes.closed') })
    if (isAdmin) s.push({ id: 'all', label: t('list.scopes.all') })
    return s
  }, [canRequest, isProjectPmAnywhere, canPurchase, isAdmin, t])

  const [scope, setScope] = useState(availableScopes[0]?.id || 'mine')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [seleccion, setSeleccion] = useState(() => new Set())     // ticked requests, for the export
  const [importando, setImportando] = useState(false)

  // Filters
  const [search, setSearch] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [projectFilter, setProjectFilter] = useState('')
  const [currencyFilter, setCurrencyFilter] = useState('')
  const [fulfillmentFilter, setFulfillmentFilter] = useState("")
  const [paymentFilter, setPaymentFilter] = useState("")
  const [soloMias, setSoloMias] = useState(false)               // quick filter: only my own requests
  const zoom = useZoom('adq_zoom_solicitudes')
  const [soloPendientes, setSoloPendientes] = useState(false)   // quick filter: the ones still moving (not closed, cancelled or rejected)
  const [agrupar, setAgrupar] = useState(null)                  // null: the scope's default (grouped by state in "Todas")
  const [porItem, setPorItem] = useState(false)               // false: one row per request; true: one row per requested item
  const [plegados, setPlegados] = useState(() => new Set())     // collapsed state groups
  const [abiertos, setAbiertos] = useState(() => new Set())      // requests whose items are showing
  const alternarItems = (id) => setAbiertos(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })

  // Mirrors the backend's default order_by(updated_at.desc()), so the first
  // render looks exactly as it did before sorting existed.
  const [sort, setSort] = useState({ key: 'updated_at', dir: 'desc' })

  const toggleSort = (key) => {
    setSort(s => s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: SORT_DEFAULT_DIR[key] })
  }

  useEffect(() => {
    if (!availableScopes.find(s => s.id === scope) && availableScopes.length) {
      setScope(availableScopes[0].id)
    }
  }, [availableScopes, scope])

  const load = async () => {
    setLoading(true)
    try {
      const data = await getPurchases({ scope })
      setItems(Array.isArray(data) ? data : [])
    } catch {
      setItems([])
    }
    setLoading(false)
  }

  useEffect(() => { load() }, [scope])
  useEffect(() => {
    getProjects().then(d => setProjects(Array.isArray(d) ? d.filter(p => p.is_active) : [])).catch(() => {})
  }, [])

  // Reset filters when scope changes
  useEffect(() => {
    setSearch(''); setStateFilter(''); setProjectFilter(''); setCurrencyFilter(''); setSoloMias(false); setSoloPendientes(false); setFulfillmentFilter(""); setPaymentFilter("")
  }, [scope])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter(p => {
      if (stateFilter && p.state !== stateFilter) return false
      if (projectFilter && p.project_reference !== projectFilter) return false
      if (currencyFilter && p.currency !== currencyFilter) return false
      if (fulfillmentFilter && p.fulfillment_status !== fulfillmentFilter) return false
      if (paymentFilter && p.payment_status !== paymentFilter) return false
      if (soloMias && p.requester_id !== user?.id) return false
      if (soloPendientes && !PENDIENTES.includes(p.state)) return false
      if (q) {
        // A digits-only query (optionally "#"-prefixed) is an order-number
        // lookup. Exact match, not prefix — otherwise "1" would return every
        // order whose id merely starts with 1, which is useless.
        const idQuery = q.replace(/^#/, '')
        if (/^\d+$/.test(idQuery)) {
          if (String(p.id) !== idQuery) return false
        } else {
          const hay = [
            p.item_description, p.id_rem, p.supplier, ...(p.items || []).flatMap(it => [it.item_description, it.id_rem]), p.project_reference,
            p.po_number, p.requester?.display_name,
          ].filter(Boolean).join(' ').toLowerCase()
          if (!hay.includes(q)) return false
        }
      }
      return true
    })
  }, [items, search, stateFilter, projectFilter, currencyFilter, soloMias, soloPendientes, fulfillmentFilter, paymentFilter, user?.id])

  // Ordering is kept separate from filtering: `filtered` decides which rows
  // show, `sorted` decides in what order. Copy before sorting — mutating the
  // memoized array in place would corrupt `filtered`.
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => compareBy(sort.key, sort.dir, a, b)),
    [filtered, sort],
  )

  const projectOptions = useMemo(() => {
    const all = new Set(items.map(p => p.project_reference).filter(Boolean))
    return Array.from(all).sort()
  }, [items])

  // Item by item: every line of the requests that pass the filters; with a text search, the lines that match it
  // (or all the lines of a request matched through its supplier, project or requester).
  const filasItems = useMemo(() => {
    const q = search.trim().toLowerCase()
    const porTexto = q && !/^#?\d+$/.test(q)
    const out = []
    for (const p of sorted) {
      const cabecera = [p.supplier, p.project_reference, p.po_number, p.requester?.display_name].filter(Boolean).join(' ').toLowerCase()
      lineasDe(p).forEach((l, i) => {
        if (porTexto && !cabecera.includes(q) && !`${l.id_rem || ''} ${l.item_description || ''}`.toLowerCase().includes(q)) return
        out.push({ p, l, i })
      })
    }
    return out
  }, [sorted, search])
  const hasFilters = search || stateFilter || projectFilter || currencyFilter || soloMias || soloPendientes || fulfillmentFilter || paymentFilter
  const agrupado = agrupar ?? (scope === 'visible')
  const grupos = useMemo(() => STATE_KEYS
    .map(k => ({ key: k, rows: sorted.filter(p => p.state === k) }))
    .filter(g => g.rows.length), [sorted])
  const alternarGrupo = (k) => setPlegados(s => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n })
  const clearFilters = () => {
    setSearch(''); setStateFilter(''); setProjectFilter(''); setCurrencyFilter(''); setSoloMias(false); setSoloPendientes(false); setFulfillmentFilter(""); setPaymentFilter("")
  }

  // Logistics and payment are edited in place by purchasing (what the old status board did).
  const patchField = async (id, data) => {
    const prev = items
    setItems(rs => rs.map(r => (r.id === id ? { ...r, ...data } : r)))
    try {
      const updated = await updatePurchaseOrderFields(id, data)
      setItems(rs => rs.map(r => (r.id === id ? updated : r)))
    } catch (e) {
      setItems(prev)
      setError(e.response?.data?.detail || e.message)
    }
  }

  const openNew = () => navigate('/purchases/new')

  // The ticked requests that are on screen; with none ticked, the export is everything the filters leave.
  const marcadas = sorted.filter(p => seleccion.has(p.id)).map(p => p.id)
  const todasMarcadas = sorted.length > 0 && marcadas.length === sorted.length
  const marcar = (id) => setSeleccion(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const marcarTodas = (on) => setSeleccion(s => { const n = new Set(s); sorted.forEach(p => (on ? n.add(p.id) : n.delete(p.id))); return n })

  const downloadHistory = async () => {
    setDownloading(true)
    try {
      await exportPurchasesExcel({
        scope,
        ...(marcadas.length ? { ids: marcadas } : {}),
        ...(stateFilter ? { state: stateFilter } : {}),
        ...(projectFilter ? { project: projectFilter } : {}),
        ...(currencyFilter ? { currency: currencyFilter } : {}),
        ...(search.trim() ? { q: search.trim() } : {}),
      })
    } catch (e) {
      setError(e.response?.data?.detail || t('modal.errors.createFailed'))
    }
    setDownloading(false)
  }

  return (
    <div className="p-8">
      {error && <div role="alert" className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">{t('list.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('list.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!hideDownload && marcadas.length > 0 && (
            <span className="text-sm text-indigo-700">
              {t('list.selected', { count: marcadas.length })}{' '}
              <button type="button" onClick={() => setSeleccion(new Set())} className="underline">{t('list.clearSelection')}</button>
            </span>
          )}
          {!hideDownload && (
            <button
              onClick={downloadHistory}
              disabled={downloading || filtered.length === 0}
              className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40 transition-colors"
              title={marcadas.length ? t('list.exportSelectedHint') : t('list.downloadHint')}
            >
              {downloading ? t('list.downloading') : marcadas.length ? t('list.exportSelected', { count: marcadas.length }) : t('list.downloadBtn')}
            </button>
          )}
          {canRequest && scope === 'mine' && (
            <button
              onClick={() => setImportando(true)}
              className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              title={t('list.importHint')}
            >
              ↑ {t('list.importBtn')}
            </button>
          )}
          {canRequest && scope === 'mine' && (
            <button
              onClick={openNew}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              {t('list.newRequest')}
            </button>
          )}
        </div>
      </div>

      {/* Scope tabs */}
      {availableScopes.length > 1 && (
        <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
          {availableScopes.map(s => (
            <button
              key={s.id}
              onClick={() => setScope(s.id)}
              className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
                scope === s.id
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <input
            type="search"
            placeholder={t('list.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
          <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </div>
        {[['soloMias', soloMias, setSoloMias], ['soloPendientes', soloPendientes, setSoloPendientes]].map(([k, on, set]) => (
          <button key={k} type="button" aria-pressed={on} onClick={() => set(!on)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            {t(`list.quick.${k}`)}
          </button>
        ))}
        <div className="inline-flex rounded-full border border-gray-200 bg-white p-0.5" role="group" aria-label={t('list.view.title')}>
          {[false, true].map(v => (
            <button key={String(v)} type="button" aria-pressed={porItem === v} onClick={() => setPorItem(v)}
              className={`px-3 py-1 rounded-full text-xs font-medium ${porItem === v ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}>
              {t(v ? 'list.view.items' : 'list.view.requests')}
            </button>
          ))}
        </div>
        <button type="button" aria-pressed={agrupado} onClick={() => setAgrupar(!agrupado)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${agrupado ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
          {t('list.quick.agrupar')}
        </button>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="text-xs text-gray-500 hover:text-gray-900 px-2 py-2"
          >
            {t('list.clearFilters')}
          </button>
        )}
        <span className="ml-auto"><ControlZoom z={zoom} /></span>
        <span className="text-xs text-gray-400 ml-auto">
          {filtered.length} of {items.length}
        </span>
      </div>

      <ZonaZoom z={zoom} libre>
      {loading ? (
        <p className="text-sm text-gray-400">{t('common:actions.loading')}</p>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-400">
          {scope === 'mine' && t('list.empty.mine')}
          {scope === 'inbox' && t('list.empty.inbox')}
          {scope === 'orders' && t('list.empty.orders')}
          {scope === 'closed' && t('list.empty.closed')}
          {scope === 'all' && t('list.empty.all')}
          {scope === 'projects' && t('list.empty.projects')}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-400">
          {t('list.noMatches')}
        </div>
      ) : porItem ? (
        filasItems.length === 0
          ? <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-400">{t('list.noMatches')}</div>
          : <TablaItems filas={filasItems} navigate={navigate} t={t} />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full min-w-[1280px] text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {!hideDownload && (
                  <th className="pl-4 pr-0 py-3 w-8">
                    <input type="checkbox" className="accent-gray-900" checked={todasMarcadas} aria-label={t('list.selectAll')} title={t('list.selectAll')}
                      ref={el => { if (el) el.indeterminate = marcadas.length > 0 && !todasMarcadas }}
                      onChange={e => marcarTodas(e.target.checked)} />
                  </th>
                )}
                <SortHeader label={t('list.cols.id')} sortKey="id" sort={sort} onSort={toggleSort} t={t} />
                <SortHeader label={t('list.cols.supplier')} sortKey="supplier" sort={sort} onSort={toggleSort} t={t} />
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">
                  {t('list.cols.project')}
                  <ColumnFilter
                    label={t('list.filter.projects')}
                    value={projectFilter}
                    options={projectOptions}
                    onChange={setProjectFilter}
                  />
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{t('list.cols.qty')}</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">
                  {t('list.cols.subtotal')}
                  <ColumnFilter
                    label={t('list.filter.currencies')}
                    value={currencyFilter}
                    options={CURRENCIES}
                    onChange={setCurrencyFilter}
                  />
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{t('list.cols.vat')}</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{t('list.cols.total')}</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">
                  {t('list.cols.state')}
                  <ColumnFilter
                    label={t('list.filter.states')}
                    value={stateFilter}
                    options={STATE_KEYS.map(s => ({ value: s, label: t(`states.${s}`) }))}
                    onChange={setStateFilter}
                  />
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">
                  {t('board.cols.fulfillmentStatus')}
                  <ColumnFilter label={t('board.filter.fulfillmentStatuses')} value={fulfillmentFilter}
                    options={FULFILLMENT_STATUSES.map(s => ({ value: s, label: t(`board.fulfillmentStatuses.${s}`) }))}
                    onChange={setFulfillmentFilter} />
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">
                  {t('board.cols.paymentStatus')}
                  <ColumnFilter label={t('board.filter.paymentStatuses')} value={paymentFilter}
                    options={PAYMENT_STATUSES.map(s => ({ value: s, label: t(`board.paymentStatuses.${s}`) }))}
                    onChange={setPaymentFilter} />
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{t('board.cols.tracking')}</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{t('list.cols.requester')}</th>
                <SortHeader label={t('list.cols.updated')} sortKey="updated_at" sort={sort} onSort={toggleSort} t={t} />
              </tr>
            </thead>
            <tbody>
              {(agrupado ? grupos : [{ key: null, rows: sorted }]).map(g => (<Fragment key={g.key || 'todas'}>
                {g.key && (
                  <tr className="bg-gray-50 border-b border-gray-100 cursor-pointer" onClick={() => alternarGrupo(g.key)}>
                    <td colSpan={14} className="px-4 py-2">
                      <span className={`inline-block transition-transform mr-2 text-[10px] text-gray-400 ${plegados.has(g.key) ? '' : 'rotate-90'}`} aria-hidden="true">▶</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATE_STYLE[g.key]}`}>{t(`states.${g.key}`)}</span>
                      <span className="ml-2 text-xs text-gray-400">{g.rows.length}</span>
                    </td>
                  </tr>
                )}
                {!plegados.has(g.key) && g.rows.map(p => {
                const totals = lineTotals(p)
                const rowCurrency = totals.currency || p.currency
                const isMixed = !!totals.mixed
                const lineCount = (p.items || []).length
                return (
                  <Fragment key={p.id}>
                  <tr
                    onClick={() => navigate(`/purchases/${p.id}`)}
                    className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors cursor-pointer last:border-b-0"
                  >
                    {!hideDownload && (
                      <td className="pl-4 pr-0 py-3 w-8" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" className="accent-gray-900" checked={seleccion.has(p.id)} onChange={() => marcar(p.id)}
                          aria-label={t('list.selectOne', { id: p.id })} />
                      </td>
                    )}
                    <td className="px-4 py-3 text-xs font-mono text-gray-400 whitespace-nowrap">#{p.id}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <button type="button" aria-expanded={abiertos.has(p.id)} aria-label={t('list.items.toggle')} title={t('list.items.toggle')}
                          onClick={e => { e.stopPropagation(); alternarItems(p.id) }}
                          className="mt-0.5 text-gray-400 hover:text-gray-900 shrink-0">
                          <span className={`inline-block transition-transform text-[10px] ${abiertos.has(p.id) ? 'rotate-90' : ''}`} aria-hidden="true">▶</span>
                        </button>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900">
                            <TruncatedCell text={p.supplier || t('list.noSupplier')} className="max-w-[16rem]" />
                          </div>
                          <div className="text-[11px] text-gray-400 mt-0.5">
                            {p.is_multi ? t('list.multipleItems', { count: lineCount }) : (p.item_description || '—')}
                          </div>
                          {p.sap_modificada && <div className="text-[11px] font-medium text-red-600" title={t('detail.sapModificadaHint')}>⚠ {t('detail.sapModificada')}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">
                        <TruncatedCell text={p.project_reference || null} className="max-w-[10rem]" />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {p.is_multi
                        ? t(lineCount === 1 ? 'list.lineCount_one' : 'list.lineCount_other', { count: lineCount })
                        : `${p.quantity}${p.unit ? ` ${p.unit}` : ''}`}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {isMixed ? <span className="text-gray-400 italic">{t('list.mixed')}</span> : fmtMoney(totals.subtotal, rowCurrency)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                      {isMixed
                        ? <span className="text-gray-400 italic">{t('list.mixed')}</span>
                        : (totals.vat == null
                            ? '—'
                            : `${fmtMoney(totals.vat, rowCurrency)} (${Math.round((totals.rate || 0) * 100)}%)`)}
                    </td>
                    <td className="px-4 py-3 text-gray-900 whitespace-nowrap font-medium">
                      {isMixed ? <span className="text-gray-400 italic">{t('list.mixed')}</span> : fmtMoney(totals.total, rowCurrency)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATE_STYLE[p.state]}`}>
                        {t(`states.${p.state}`) || p.state}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      {/* Only meaningful while "ordered": otherwise there is nothing to tag yet. */}
                      <PillCell value={p.fulfillment_status} options={FULFILLMENT_STATUSES} styleMap={FULFILLMENT_STYLE}
                        editable={canPurchase && p.state === 'ordered'} clearLabel={t('board.clear')}
                        label={v => t(`board.fulfillmentStatuses.${v}`, { defaultValue: v })}
                        onSelect={v => patchField(p.id, { fulfillment_status: v })} />
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <PillCell value={p.payment_status} options={PAYMENT_STATUSES} styleMap={PAYMENT_STYLE}
                        editable={canPurchase && ['approved', 'sap', 'ordered', 'received', 'closed'].includes(p.state)}
                        label={v => t(`board.paymentStatuses.${v}`, { defaultValue: v })}
                        onSelect={v => patchField(p.id, { payment_status: v })} />
                    </td>
                    <td className="px-4 py-3 text-xs" onClick={e => e.stopPropagation()}>
                      <TextCell value={p.tracking} editable={canPurchase && ['sap', 'ordered', 'received'].includes(p.state)}
                        placeholder={t('board.trackingPlaceholder')} onSave={v => patchField(p.id, { tracking: v })} />
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <TruncatedCell text={p.requester?.display_name || null} className="max-w-[10rem]" />
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDate(p.updated_at)}</td>
                  </tr>
                  {abiertos.has(p.id) && (
                    <tr className="bg-gray-50/70 border-b border-gray-100">
                      <td colSpan={14} className="px-6 py-3">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-gray-400">
                              <th className="font-medium pr-3 py-1">{t('list.items.code')}</th>
                              <th className="font-medium pr-3 py-1">{t('list.items.description')}</th>
                              <th className="font-medium pr-3 py-1">{t('list.items.qty')}</th>
                              <th className="font-medium pr-3 py-1">{t('list.items.price')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lineasDe(p).map((it, i) => (
                              <tr key={i} className="border-t border-gray-100">
                                <td className="pr-3 py-1 font-mono text-gray-500 whitespace-nowrap">{it.id_rem || '—'}</td>
                                <td className="pr-3 py-1 text-gray-800">{it.item_description || '—'}</td>
                                <td className="pr-3 py-1 text-gray-600 whitespace-nowrap">{it.quantity ?? '—'}{it.unit ? ` ${it.unit}` : ''}</td>
                                <td className="pr-3 py-1 text-gray-600 whitespace-nowrap">{(it.final_unit_price ?? it.estimated_unit_price) != null ? fmtMoney(it.final_unit_price ?? it.estimated_unit_price, it.currency || p.currency) : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                )
              })}</Fragment>))}
            </tbody>
          </table>
        </div>
      )}
      </ZonaZoom>
      {importando && <ImportarSolicitudes onClose={() => setImportando(false)} onDone={load} />}
    </div>
  )
}
